import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOptionalSupplierPageContext } from '@/contexts/SupplierPageContext';

export const useSupplierPageId = (supplierProfileId: string | undefined) => {
  const pageContext = useOptionalSupplierPageContext();

  const [supplierPageId, setSupplierPageId] = useState<string | null>(null);
  const [supplierPageSlug, setSupplierPageSlug] = useState<string | null>(null);
  const [supplierPageCategory, setSupplierPageCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Whether we have a context with an active page
  const contextActivePage = pageContext?.activePage ?? null;
  const contextLoading = pageContext?.loading ?? false;

  useEffect(() => {
    // If context is available and has an active page, use it directly
    if (contextActivePage) {
      setSupplierPageId(contextActivePage.id);
      setSupplierPageSlug(contextActivePage.slug);
      setSupplierPageCategory(contextActivePage.sub_category);
      setLoading(false);
      setError(null);
      return;
    }

    // If context exists but is still loading, wait
    if (pageContext !== undefined && contextLoading) {
      setLoading(true);
      return;
    }

    // Fallback: query directly (for components outside the provider, e.g. Navigation)
    if (!supplierProfileId) {
      setSupplierPageId(null);
      setSupplierPageSlug(null);
      setSupplierPageCategory(null);
      setLoading(false);
      setError(null);
      return;
    }

    // Only do the direct query if we're outside the context provider
    if (pageContext !== undefined) {
      // We're inside the provider but no active page (no pages exist)
      setSupplierPageId(null);
      setSupplierPageSlug(null);
      setSupplierPageCategory(null);
      setLoading(false);
      return;
    }

    const fetchSupplierPageId = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from('supplier_pages')
          .select('id, slug, sub_category')
          .eq('supplier_id', supplierProfileId)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching supplier page ID:', error);
          setError(error.message);
          setSupplierPageId(null);
          setSupplierPageSlug(null);
          setSupplierPageCategory(null);
        } else {
          setSupplierPageId(data?.id || null);
          setSupplierPageSlug(data?.slug || null);
          setSupplierPageCategory(data?.sub_category || null);
        }
      } catch (err) {
        console.error('Error fetching supplier page ID:', err);
        setError('Failed to fetch supplier page ID');
        setSupplierPageId(null);
        setSupplierPageSlug(null);
        setSupplierPageCategory(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSupplierPageId();
  }, [supplierProfileId, contextActivePage?.id, contextLoading, pageContext]);

  return { supplierPageId, supplierPageSlug, supplierPageCategory, loading, error };
};
